"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { Locale } from "@/i18n-config";
import { getCoursesMarketplaceCopy } from "@/content/courses-marketplace-copy";
import { getWishlistCopy } from "@/content/wishlist-copy";
import { Course } from "@/types";
import { cn } from "@/utils/cn";
import { toSanityOriginalImageUrl } from "@/lib/sanity/image";
import { appendVideoStartFragment, resolveCoursePreviewVideo } from "@/lib/courses/video-preview";
import { useWishlist } from "@/hooks/use-wishlist";
import { WishlistHeartButton } from "@/components/wishlist/wishlist-heart-button";

type CategoryKey = "coding" | "practice" | "mindset";
type DifficultyKey = "beginner" | "standard" | "advanced";
type TimeKey = "lt30" | "min30to60" | "hr1to2";
type AccessKey = "free" | "paid";
type SortKey = "recommended" | "shortest" | "beginnerFriendly" | "newest";

type CourseCardCover = {
  src: string | null;
  alt: string;
  isExternal: boolean;
  videoSrc: string | null;
  videoThumbnailSrc: string | null;
  hasEmbeddedVideo: boolean;
};

type CourseMarketplaceItem = {
  course: Course;
  category: CategoryKey;
  difficulty: DifficultyKey;
  durationMinutes: number;
  timeBucket: TimeKey;
  access: AccessKey;
  outcomeLine: string;
  cover: CourseCardCover;
  createdAtTs: number;
  sourceIndex: number;
};

const difficultyRank: Record<DifficultyKey, number> = {
  beginner: 0,
  standard: 1,
  advanced: 2,
};

const INITIAL_TILE_BATCH = 12;
const TILE_BATCH_STEP = 8;

function formatTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ""));
}

function toggleOption<T extends string>(prev: T[], value: T): T[] {
  return prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value];
}

function inferDuration(course: Course): number {
  if (
    typeof course.durationMinutes === "number" &&
    Number.isFinite(course.durationMinutes) &&
    course.durationMinutes > 0
  ) {
    return Math.round(course.durationMinutes);
  }

  const lessonCount = course.lessons?.length ?? 0;
  if (lessonCount > 0) {
    return Math.max(20, lessonCount * 20);
  }

  return 45;
}

function inferTimeBucket(durationMinutes: number): TimeKey {
  if (durationMinutes < 30) return "lt30";
  if (durationMinutes <= 60) return "min30to60";
  return "hr1to2";
}

function inferDifficulty(course: Course, durationMinutes: number): DifficultyKey {
  const normalizedDifficulty = (course.difficulty ?? "").trim().toLowerCase();
  if (normalizedDifficulty === "beginner") return "beginner";
  if (normalizedDifficulty === "standard" || normalizedDifficulty === "intermediate") return "standard";
  if (normalizedDifficulty === "advanced") return "advanced";

  const source = `${course.difficulty ?? ""} ${course.title ?? ""} ${course.description ?? ""}`.toLowerCase();

  if (/(begin|starter|basic|intro|easy|novice|начал|нович|почат|базов)/.test(source)) {
    return "beginner";
  }

  if (/(adv|expert|hard|pro|advanced|слож|продвин|просун)/.test(source)) {
    return "advanced";
  }

  if (/(inter|medium|middle|средн|проміж|помір)/.test(source)) {
    return "standard";
  }

  if (durationMinutes >= 90) return "advanced";
  if (durationMinutes >= 45) return "standard";
  return "beginner";
}

function inferCategory(course: Course): CategoryKey {
  const source = `${course.category ?? ""} ${course.section ?? ""} ${course.title ?? ""} ${course.description ?? ""}`.toLowerCase();

  if (/(mindset|mind|clarity|focus|decision|overwhelm|confidence|motivation|мыш|мисл|підхід|подход)/.test(source)) {
    return "mindset";
  }

  if (/(practice|project|build|launch|workflow|execute|ship|apply|практик|реал|запуск|кейс)/.test(source)) {
    return "practice";
  }

  if (/(coding|code|program|script|api|frontend|backend|dev|website|app|automation|бот|код|розроб|разраб)/.test(source)) {
    return "coding";
  }

  return "practice";
}

function getCreatedAtTimestamp(course: Course): number {
  const timestamp = Date.parse(course._createdAt ?? "");
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function toSingleLine(text: string, maxLength = 110): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
}

function toSanityImageUrl(source: Parameters<typeof toSanityOriginalImageUrl>[0]): string | null {
  return toSanityOriginalImageUrl(source);
}

function resolveCourseCover(course: Course): CourseCardCover {
  const altFallback = `${course.title} cover`;
  const previewVideo = resolveCoursePreviewVideo(course.coursePreviewVideo);

  if (course.courseCoverImage?.source === "url" && course.courseCoverImage.externalUrl) {
    return {
      src: course.courseCoverImage.externalUrl,
      alt: toSingleLine(course.courseCoverImage.alt ?? altFallback, 140) || altFallback,
      isExternal: true,
      videoSrc: null,
      videoThumbnailSrc: null,
      hasEmbeddedVideo: false,
    };
  }

  const uploadedCover = toSanityImageUrl(course.courseCoverImage?.image);
  if (uploadedCover) {
    return {
      src: uploadedCover,
      alt: toSingleLine(course.courseCoverImage?.alt ?? altFallback, 140) || altFallback,
      isExternal: false,
      videoSrc: null,
      videoThumbnailSrc: null,
      hasEmbeddedVideo: false,
    };
  }

  return {
    src: null,
    alt: altFallback,
    isExternal: false,
    videoSrc: previewVideo?.src ?? null,
    videoThumbnailSrc: previewVideo?.thumbnailSrc ?? null,
    hasEmbeddedVideo: Boolean(previewVideo?.embedUrl),
  };
}

function buildOutcomeLine(course: Course, fallback: string): string {
  const preferred = course.shortOutcomeLine ?? course.description ?? "";
  const normalized = toSingleLine(preferred, 108);
  if (normalized.length > 0) return normalized;
  return fallback;
}

type CoursesMarketplaceProps = {
  courses: Course[];
  lang: Locale;
  balanceCredits?: number | null;
  isAuthenticated?: boolean;
  initialWishlistSlugs?: string[];
};

export function CoursesMarketplace({
  courses,
  lang,
  balanceCredits = null,
  isAuthenticated = false,
  initialWishlistSlugs = [],
}: CoursesMarketplaceProps) {
  const copy = getCoursesMarketplaceCopy(lang);
  const wishlistCopy = getWishlistCopy(lang);
  const wishlist = useWishlist({
    lang,
    isAuthenticated,
    initialSlugs: initialWishlistSlugs,
  });

  const [selectedCategories, setSelectedCategories] = useState<CategoryKey[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<TimeKey[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<DifficultyKey[]>([]);
  const [selectedAccess, setSelectedAccess] = useState<AccessKey[]>([]);
  const [sortBy, setSortBy] = useState<SortKey>("recommended");
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_TILE_BATCH);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const courseItems = useMemo<CourseMarketplaceItem[]>(() => {
    return courses.map((course, sourceIndex) => {
      const durationMinutes = inferDuration(course);
      const difficulty = inferDifficulty(course, durationMinutes);
      const category = inferCategory(course);
      const access: AccessKey = course.isFree ? "free" : "paid";

      return {
        course,
        category,
        difficulty,
        durationMinutes,
        timeBucket: inferTimeBucket(durationMinutes),
        access,
        outcomeLine: buildOutcomeLine(course, copy.problemFallback),
        cover: resolveCourseCover(course),
        createdAtTs: getCreatedAtTimestamp(course),
        sourceIndex,
      };
    });
  }, [courses, copy.problemFallback]);

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedTimes.length > 0 ||
    selectedDifficulties.length > 0 ||
    selectedAccess.length > 0;
  const activeFilterCount =
    selectedCategories.length + selectedTimes.length + selectedDifficulties.length + selectedAccess.length;

  const filteredItems = useMemo(() => {
    return courseItems.filter((item) => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(item.category)) return false;
      if (selectedTimes.length > 0 && !selectedTimes.includes(item.timeBucket)) return false;
      if (selectedDifficulties.length > 0 && !selectedDifficulties.includes(item.difficulty)) return false;
      if (selectedAccess.length > 0 && !selectedAccess.includes(item.access)) return false;
      return true;
    });
  }, [courseItems, selectedCategories, selectedTimes, selectedDifficulties, selectedAccess]);

  const sortedItems = useMemo(() => {
    const list = [...filteredItems];

    if (sortBy === "shortest") {
      list.sort((a, b) => a.durationMinutes - b.durationMinutes || a.course.title.localeCompare(b.course.title));
      return list;
    }

    if (sortBy === "beginnerFriendly") {
      list.sort(
        (a, b) =>
          difficultyRank[a.difficulty] - difficultyRank[b.difficulty] ||
          a.durationMinutes - b.durationMinutes ||
          a.course.title.localeCompare(b.course.title)
      );
      return list;
    }

    if (sortBy === "newest") {
      list.sort(
        (a, b) =>
          b.createdAtTs - a.createdAtTs ||
          a.durationMinutes - b.durationMinutes ||
          a.course.title.localeCompare(b.course.title)
      );
      return list;
    }

    list.sort((a, b) => a.sourceIndex - b.sourceIndex);
    return list;
  }, [filteredItems, sortBy]);

  const visibleItems = useMemo(() => sortedItems.slice(0, visibleCount), [sortedItems, visibleCount]);
  const hasMoreItems = visibleCount < sortedItems.length;

  const resultsText = formatTemplate(copy.resultsLabel, { count: sortedItems.length });

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedTimes([]);
    setSelectedDifficulties([]);
    setSelectedAccess([]);
  };

  useEffect(() => {
    if (!isMobileFiltersOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileFiltersOpen]);

  useEffect(() => {
    if (!isMobileFiltersOpen) return;

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileFiltersOpen(false);
      }
    };

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const onMediaChange = () => {
      if (mediaQuery.matches) {
        setIsMobileFiltersOpen(false);
      }
    };

    window.addEventListener("keydown", onEscape);
    mediaQuery.addEventListener("change", onMediaChange);

    return () => {
      window.removeEventListener("keydown", onEscape);
      mediaQuery.removeEventListener("change", onMediaChange);
    };
  }, [isMobileFiltersOpen]);

  useEffect(() => {
    setVisibleCount(Math.min(INITIAL_TILE_BATCH, sortedItems.length));
  }, [sortedItems]);

  useEffect(() => {
    if (!hasMoreItems) return;
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        setVisibleCount((prev) => Math.min(prev + TILE_BATCH_STEP, sortedItems.length));
      },
      { rootMargin: "240px 0px" }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreItems, sortedItems.length]);

  const filterButtonClass = (isActive: boolean) =>
    cn(
      "w-full rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
      isActive
        ? "border-[#8cb6af] bg-[#edf6f4] text-[#27443e]"
        : "border-[#ddd2bf] bg-[#fffdf8] text-[#5d6e68] hover:border-[#c5b398]"
    );

  const filterPanelContent = (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6a7a74]">{copy.filtersTitle}</h2>
        <button
          type="button"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className={cn(
            "text-[11px] transition-colors",
            hasActiveFilters ? "text-[#4f615b] hover:text-[#2d433d]" : "cursor-not-allowed text-[#a2aca7]"
          )}
        >
          {copy.clearFilters}
        </button>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7b8984]">{copy.filters.category}</p>
          <div className="space-y-1.5">
            {([
              ["coding", copy.categories.coding],
              ["practice", copy.categories.practice],
              ["mindset", copy.categories.mindset],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={selectedCategories.includes(value)}
                onClick={() => setSelectedCategories((prev) => toggleOption(prev, value))}
                className={filterButtonClass(selectedCategories.includes(value))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7b8984]">{copy.filters.time}</p>
          <div className="space-y-1.5">
            {([
              ["lt30", copy.timeOptions.lt30],
              ["min30to60", copy.timeOptions.min30to60],
              ["hr1to2", copy.timeOptions.hr1to2],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={selectedTimes.includes(value)}
                onClick={() => setSelectedTimes((prev) => toggleOption(prev, value))}
                className={filterButtonClass(selectedTimes.includes(value))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7b8984]">
            {copy.filters.difficulty}
          </p>
          <div className="space-y-1.5">
            {([
              ["beginner", copy.difficultyOptions.beginner],
              ["standard", copy.difficultyOptions.standard],
              ["advanced", copy.difficultyOptions.advanced],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={selectedDifficulties.includes(value)}
                onClick={() => setSelectedDifficulties((prev) => toggleOption(prev, value))}
                className={filterButtonClass(selectedDifficulties.includes(value))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7b8984]">{copy.filters.access}</p>
          <div className="space-y-1.5">
            {([
              ["free", copy.accessOptions.free],
              ["paid", copy.accessOptions.paid],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={selectedAccess.includes(value)}
                onClick={() => setSelectedAccess((prev) => toggleOption(prev, value))}
                className={filterButtonClass(selectedAccess.includes(value))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <section className="relative min-h-screen overflow-hidden px-0 pb-20 pt-24 sm:px-4 sm:pt-28">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_10%_12%,rgba(180,83,9,0.14),transparent_34%),radial-gradient(circle_at_84%_16%,rgba(15,118,110,0.14),transparent_34%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(rgba(29,38,34,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(29,38,34,0.05)_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_88%_72%_at_50%_0%,#000_72%,transparent_100%)] opacity-30" />

      <div className="container relative max-w-7xl space-y-6 sm:space-y-8">
        <header className="space-y-2 px-4 sm:space-y-3 sm:px-0">
          <h1 className="text-3xl font-bold text-[#1b2220] sm:text-4xl md:text-5xl">{copy.title}</h1>
          <p className="text-base text-[#3f514b] sm:text-lg">{copy.subtitle}</p>
        </header>

        <div className="grid gap-7 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="hidden h-fit rounded-2xl border border-[#ddd2bf] bg-[#fbf7ee] p-4 lg:sticky lg:top-24 lg:block">
            {filterPanelContent}
          </aside>

          <div className="space-y-4 px-2 sm:px-0">
            <div className="sticky top-16 z-30 mx-2 rounded-xl border border-[#d8cab2] bg-[#fffaf1]/95 px-3 py-2 shadow-[0_4px_14px_rgba(31,44,39,0.08)] backdrop-blur-[3px] sm:mx-0 lg:hidden">
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsMobileFiltersOpen(true)}
                  aria-controls="mobile-courses-filters"
                  aria-expanded={isMobileFiltersOpen}
                  className="inline-flex min-h-11 items-center justify-between gap-2 rounded-xl border border-[#d1c4ad] bg-white px-3 text-sm font-semibold text-[#2d433d] outline-none transition-colors hover:border-[#b79f79] focus:border-[#0f766e]"
                >
                  <span className="inline-flex items-center gap-2">
                    <Menu className="h-4 w-4" />
                    {copy.openFilters}
                  </span>
                  {hasActiveFilters ? (
                    <span className="rounded-full border border-[#8cb6af] bg-[#edf6f4] px-2 py-0.5 text-[11px] font-semibold text-[#2b5750]">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </button>

                <div className="flex min-h-11 items-center rounded-xl border border-[#d1c4ad] bg-white px-3 text-sm">
                  <label htmlFor="courses-sort-mobile" className="sr-only">
                    {copy.sortLabel}
                  </label>
                  <select
                    id="courses-sort-mobile"
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value as SortKey)}
                    className="h-full w-full bg-transparent text-sm font-semibold text-[#2e3f39] outline-none"
                  >
                    <option value="recommended">{copy.sort.recommended}</option>
                    <option value="shortest">{copy.sort.shortest}</option>
                    <option value="beginnerFriendly">{copy.sort.beginnerFriendly}</option>
                    <option value="newest">{copy.sort.newest}</option>
                  </select>
                </div>
              </div>
              <div className="mt-2.5">
                <p className="text-sm font-medium text-[#42564f]">{resultsText}</p>
                <p className="text-[11px] text-[#6f7f79]">{copy.helper}</p>
              </div>
            </div>

            <div className="hidden flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#d8cab2] bg-[#fffaf1] px-4 py-3 lg:flex">
              <div>
                <p className="text-sm font-medium text-[#42564f]">{resultsText}</p>
                <p className="text-xs text-[#6f7f79]">{copy.helper}</p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor="courses-sort" className="text-[#61736c]">
                  {copy.sortLabel}
                </label>
                <select
                  id="courses-sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value as SortKey)}
                  className="rounded-lg border border-[#d1c4ad] bg-white px-3 py-1.5 text-sm text-[#2e3f39] outline-none focus:border-[#0f766e]"
                >
                  <option value="recommended">{copy.sort.recommended}</option>
                  <option value="shortest">{copy.sort.shortest}</option>
                  <option value="beginnerFriendly">{copy.sort.beginnerFriendly}</option>
                  <option value="newest">{copy.sort.newest}</option>
                </select>
              </div>
            </div>

            {sortedItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-2.5 sm:gap-4 xl:grid-cols-3">
                  {visibleItems.map((item) => {
                    const difficultyLabel = copy.difficultyOptions[item.difficulty];
                    const categoryLabel =
                      item.course.category?.trim()
                        ? item.course.category.trim()
                        : item.category === "coding"
                        ? copy.categories.coding
                        : item.category === "practice"
                        ? copy.categories.practice
                        : copy.categories.mindset;

                    const accessBadgeLabel = item.access === "free" ? "Free" : "Paid";

                    return (
                      <article
                        key={item.course._id}
                        className="flex h-full min-h-96 flex-col overflow-hidden rounded-xl border border-[#d8cab2] bg-[#fffaf1] shadow-[0_6px_14px_rgba(34,47,42,0.06)] transition-colors hover:border-[#baa37f] sm:min-h-0 sm:rounded-2xl"
                      >
                        <div className="flex items-start justify-between gap-3 border-b border-[#e3d7c4] bg-[#fff9ef] px-3 py-2.5 sm:px-4 sm:py-3">
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold leading-tight text-[#2f433d] sm:text-xs">
                              {item.durationMinutes} {copy.durationUnit}
                            </p>
                            <p className="mt-1 text-[11px] leading-tight text-[#5a6d66] sm:text-xs">
                              {difficultyLabel}
                            </p>
                          </div>
                          <span className="inline-flex items-center rounded-md border border-[#d1c4ad] bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#42564f] sm:text-[11px]">
                            {accessBadgeLabel}
                          </span>
                        </div>

                        <div className="relative h-32 w-full overflow-hidden border-b border-[#dccfb9] bg-[#efe4d2] sm:h-auto sm:aspect-video">
                          <div className="absolute right-2 top-2 z-10">
                            <WishlistHeartButton
                              courseSlug={item.course.slug.current}
                              isWished={wishlist.isWished(item.course.slug.current)}
                              size="sm"
                              variant="overlay"
                              disabled={wishlist.isPending(item.course.slug.current)}
                              addAriaLabel={wishlistCopy.addAria}
                              removeAriaLabel={wishlistCopy.removeAria}
                              onToggle={(slug) => {
                                void wishlist.toggle(slug);
                              }}
                            />
                          </div>

                          {item.cover.src ? (
                            item.cover.isExternal ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={item.cover.src}
                                alt={item.cover.alt}
                                className="h-full w-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <Image
                                src={item.cover.src}
                                alt={item.cover.alt}
                                fill
                                unoptimized
                                sizes="(min-width: 1280px) 31vw, (min-width: 640px) 48vw, 50vw"
                                className="object-cover"
                                loading="lazy"
                              />
                            )
                          ) : item.cover.videoThumbnailSrc ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={item.cover.videoThumbnailSrc}
                              alt={item.cover.alt}
                              className="h-full w-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : item.cover.videoSrc && !item.cover.hasEmbeddedVideo ? (
                            <video
                              className="h-full w-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                              src={appendVideoStartFragment(item.cover.videoSrc)}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-[#efe4d2] text-xs font-semibold tracking-[0.08em] text-[#7d6f59]">
                              NO PREVIEW
                            </div>
                          )}
                        </div>

                        <div className="flex flex-1 flex-col p-3.5 sm:p-4">
                          <h3 className="line-clamp-2 h-[2.6rem] overflow-hidden text-ellipsis text-sm font-semibold leading-snug text-[#1f2a27] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:h-auto sm:min-h-[2.8rem] sm:text-base">
                            {item.course.title}
                          </h3>

                          <p className="mt-2 line-clamp-2 h-[2.9rem] overflow-hidden text-ellipsis text-xs leading-relaxed text-[#4d6059] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:mt-2 sm:h-auto sm:min-h-[2.8rem] sm:text-sm">
                            {item.outcomeLine}
                          </p>

                          <p className="mt-2.5 min-h-[1.1rem] truncate text-[11px] text-[#5a6d66] sm:mt-4 sm:min-h-0 sm:text-xs">
                            {categoryLabel}
                          </p>

                          <Link
                            href={`/${lang}/courses/${item.course.slug.current}`}
                            prefetch={false}
                            className="mt-auto inline-flex min-h-9 w-full items-center justify-center rounded-lg border border-[#cbbca2] bg-white px-3 text-xs font-semibold text-[#29453f] outline-none transition-colors hover:border-[#aa9471] hover:bg-[#fbf6eb] focus-visible:border-[#0f766e] focus-visible:ring-2 focus-visible:ring-[#0f766e]/25 sm:mt-4 sm:min-h-11 sm:rounded-xl sm:px-4 sm:text-sm"
                          >
                            {lang === "en" ? "View course" : copy.viewDetails}
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {hasMoreItems ? (
                  <div className="space-y-2">
                    <div ref={loadMoreRef} className="h-2 w-full" />
                    <div className="grid grid-cols-2 gap-2.5 sm:gap-4 xl:grid-cols-3" aria-hidden="true">
                      {Array.from({ length: Math.min(3, sortedItems.length - visibleItems.length) }).map((_, index) => (
                        <div
                          key={`course-skeleton-${index}`}
                          className="overflow-hidden rounded-xl border border-[#e1d6c4] bg-[#fffaf1] sm:rounded-2xl"
                        >
                          <div className="flex items-start justify-between gap-3 border-b border-[#ebe0cf] bg-[#fff9ef] px-3 py-2.5 sm:px-4 sm:py-3">
                            <div className="space-y-1">
                              <div className="h-2.5 w-14 animate-pulse rounded bg-[#ece2d3]" />
                              <div className="h-2.5 w-12 animate-pulse rounded bg-[#ece2d3]" />
                            </div>
                            <div className="h-5 w-12 animate-pulse rounded-md bg-[#ece2d3]" />
                          </div>
                          <div className="h-32 w-full animate-pulse bg-[#ece2d3] sm:h-auto sm:aspect-video" />
                          <div className="space-y-2 p-3 sm:p-4">
                            <div className="h-3 w-5/6 animate-pulse rounded bg-[#ece2d3]" />
                            <div className="h-3 w-4/6 animate-pulse rounded bg-[#ece2d3]" />
                            <div className="h-8 w-full animate-pulse rounded bg-[#ece2d3]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-[#d2c3ab] bg-[#fff8ec] p-12 text-center text-sm text-[#566962]">
                {copy.empty}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!isMobileFiltersOpen}
        aria-label={copy.filtersTitle}
        className={cn(
          "fixed inset-0 z-[90] lg:hidden",
          "transition-opacity duration-300 ease-out",
          isMobileFiltersOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <button
          type="button"
          aria-label={copy.closeFilters}
          onClick={() => setIsMobileFiltersOpen(false)}
          className={cn(
            "absolute inset-0 bg-[#182320]/45 backdrop-blur-[2px] transition-opacity duration-300 ease-out",
            isMobileFiltersOpen ? "opacity-100" : "opacity-0"
          )}
        />
        <div
          id="mobile-courses-filters"
          className={cn(
            "absolute inset-y-0 left-0 w-[86vw] max-w-sm overflow-y-auto border-r border-[#d8cab2] bg-[#fbf7ee] p-4 shadow-[14px_0_32px_rgba(27,38,34,0.24)]",
            "transform-gpu transition-transform duration-300 ease-out",
            isMobileFiltersOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#2f433d]">{copy.openFilters}</p>
            <button
              type="button"
              aria-label={copy.closeFilters}
              onClick={() => setIsMobileFiltersOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#d6c8b0] bg-[#fffdf8] text-[#42564f] transition-colors hover:border-[#bea985]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {filterPanelContent}
        </div>
      </div>
    </section>
  );
}
